import type { HandlerOptions } from '@/interfaces/common.interface';
import type { CreateAccountDTO, UpdateAccountDTO } from '@/services/cxm.service.interface';
import { useAuthStore } from '@/stores/auth.store';
import { handleAxiosError } from '@/utils/error.utils';
import { toast } from '@/utils/toast.utils';

export const useCurrentAccount = () => {
  const authStore = useAuthStore();
  const { t } = useTranslations();

  const account = computed(() => authStore.currentAccount);

  /**
   * Handles the creation of a new account.
   * @param {CreateAccountDTO} data - The data for creating the account.
   * @param {HandlerOptions} [options] - Optional parameters.
   * @param {boolean} [options.silent=false] - Whether to suppress toast notifications.
   * @throws {Error} If the account creation fails.
   */
  async function onCreateAccountHandler(
    data: CreateAccountDTO,
    options: HandlerOptions = { silent: false },
  ) {
    const loadingToast = !options.silent
      ? toast.loading({ message: t('notifications.create_account_loading') })
      : null;
    try {
      await authStore.createAccount(data);

      loadingToast?.resolve({ message: t('notifications.create_account_success') });
    } catch (error) {
      const { message, data: errorData } = handleAxiosError(error);

      loadingToast?.error({ message: (errorData as string) ?? message });

      throw new Error((errorData as string) ?? message);
    }
  }

  /**
   * Handles changing the current account.
   * @param {number} account_id - The ID of the account to switch to.
   * @param {HandlerOptions} [options] - Optional parameters.
   * @param {boolean} [options.silent=false] - Whether to suppress toast notifications.
   */
  async function onChangeAccountHandler(
    account_id: number,
    options: HandlerOptions = { silent: false },
  ) {
    const loadingToast = !options.silent
      ? toast.loading({ message: t('notifications.change_account_loading') })
      : null;
    try {
      await authStore.setCurrentAccount(account_id);

      loadingToast?.resolve({ message: t('notifications.change_account_success') });
    } catch (error) {
      const { message } = handleAxiosError(error);
      loadingToast?.error({ message });
      throw error;
    }
  }

  /**
   * Handles updating the current account information.
   * @param {Partial<UpdateAccountDTO>} data - The data to update the account with.
   * @param {HandlerOptions} [options] - Optional parameters.
   * @param {boolean} [options.silent=false] - Whether to suppress toast notifications.
   */
  async function onUpdateAccountHandler(
    data: Partial<UpdateAccountDTO>,
    options: HandlerOptions = { silent: false },
  ) {
    const loadingToast = !options.silent
      ? toast.loading({ message: t('notifications.update_information_loading') })
      : null;
    try {
      await authStore.updateCurrentAccount(data);

      loadingToast?.resolve({ message: t('notifications.update_information_success') });
    } catch (error) {
      const { message } = handleAxiosError(error);

      loadingToast?.error({ message });
      throw error;
    }
  }

  return {
    //data
    account,
    accounts: computed(() => authStore.user?.accounts),
    logo: computed(
      () =>
        account.value?.logo?.original?.url ?? '/images/placeholders/account-logo-placeholder.webp',
    ),
    currency: computed(() => account.value?.partner.currency),
    //methods
    onCreateAccountHandler,
    onChangeAccountHandler,
    onUpdateAccountHandler,
  };
};
